import { nanoid } from 'nanoid'
import camelCase from 'lodash/camelCase.js'
import toLower from 'lodash/toLower.js'

const listableNodeTypes = ['noteBoardNote']

export function enhanceTipTapPlainHtml(html, bucketName, isPasted = false) {
  const fragment = document.createDocumentFragment()
  const div = document.createElement('div')
  div.innerHTML = html
  fragment.appendChild(div)
  for (let i = 0; i < div.childNodes.length; i++) {
    const child = div.childNodes[i]
    processElement(div, child, bucketName, isPasted)
  }
  return div.innerHTML
}

function processElement(parent, element, bucketName, isPasted) {
  const tagName = camelCase(toLower(element.tagName))
  if (newExtensionHandlers[tagName]) {
    newExtensionHandlers[tagName](parent, element, bucketName, isPasted)
  } else {
    for (let i = 0; i < element.childNodes.length; i++) {
      const child = element.childNodes[i]
      if (child.nodeType === Node.ELEMENT_NODE) {
        processElement(element, child, bucketName, isPasted)
      }
    }
  }
}

const customListHandler = (parent, ul) => {
  // Turn the ul into a flattened list of custom-list elements, and insert them
  // before the ul
  const listItems = collectListItems(ul)
  for (let i = 0; i < listItems.length; i++) {
    const item = listItems[i]
    parent.insertBefore(item, ul)
  }
  parent.removeChild(ul)
}

const newExtensionHandlers = {
  img: (parent, img, bucketName) => {
    const bucketUrl = `https://firebasestorage.googleapis.com/v0/b/${bucketName}/o/`
    const scale = img.getAttribute('scale') || 1
    const src = img.getAttribute('src')

    if (src) {
      if (src.startsWith(`${bucketUrl}`)) {
        const path = decodeURIComponent(
          src.slice(bucketUrl.length).split('?')[0],
        ).split('/')
        const fileId = path[0]
        const replacementImg = document.createElement('bucket-file')
        replacementImg.setAttribute('scale', scale)
        replacementImg.setAttribute('file-id', fileId)
        parent.insertBefore(replacementImg, img)
        parent.removeChild(img)
      } else {
        const replacementImg = document.createElement('pasted-image')
        replacementImg.setAttribute('src', src)
        if (scale) {
          replacementImg.setAttribute('scale', scale)
        }
        parent.insertBefore(replacementImg, img)
        parent.removeChild(img)
      }
    }
  },
  noteBoardNote: (parent, noteBoardNote, bucketName, isPasted) => {
    if (isPasted) {
      // If it's pasted, we want to replace the note-board-note with a
      // note-board-note-placeholder element, which will be replaced with the
      // actual note-board-note when the note is loaded, and which will
      // clear any duplicate notes
      const noteBoardNotePlaceholder = document.createElement(
        'note-board-note-placeholder',
      )
      noteBoardNotePlaceholder.setAttribute(
        'note-id',
        noteBoardNote.getAttribute('note-id'),
      )
      noteBoardNotePlaceholder.setAttribute(
        'note-board-id',
        noteBoardNote.getAttribute('note-board-id'),
      )
      noteBoardNotePlaceholder.setAttribute(
        'research-notebook-id',
        noteBoardNote.getAttribute('research-notebook-id'),
      )
      noteBoardNotePlaceholder.setAttribute(
        'research-source-id',
        noteBoardNote.getAttribute('research-source-id'),
      )
      parent.insertBefore(noteBoardNotePlaceholder, noteBoardNote)
      parent.removeChild(noteBoardNote)
    } else {
      // This merely replaces the content of the note-board-note element with an
      // empty string.  It will be automatically filled in by the note's
      // component when it is rendered
      noteBoardNote.childNodes[0].innerHTML = ''
    }
  },
  ul: customListHandler,
  ol: customListHandler,
}

function collectListItems(ulOrOl, currentDepth = 0) {
  const result = []
  const parentTagName = camelCase(toLower(ulOrOl.tagName))
  const listId = ulOrOl.getAttribute('data-custom-list-id') || nanoid()
  for (let i = 0; i < ulOrOl.childNodes.length; i++) {
    // a <li> element only counts as a list item if it begins with a non-ul and non-ol
    // element
    const listItem = ulOrOl.childNodes[i]
    const listItemStyleType =
      listItem.getAttribute('data-custom-list-style-type') ||
      (parentTagName === 'ol' ? 'ordered1' : 'unordered1')
    for (let j = 0; j < listItem.childNodes.length; j++) {
      const child = listItem.childNodes[j]
      const tagName = camelCase(toLower(child.tagName))
      if (listableNodeTypes.indexOf(tagName) > -1) {
        // this is a list item, just shove it in the result
        result.push(child)
      } else if (tagName === 'ul' || tagName === 'ol') {
        // Otherwise it's a nested list, so recursively collect the list items
        const childListItems = collectListItems(child, currentDepth + 1)
        for (let k = 0; k < childListItems.length; k++) {
          const childListItem = childListItems[k]
          result.push(childListItem)
        }
      } else {
        // this is the contents of a actual custom-list element, so create a custom-list
        // element and set its contents to the child, as well as its depth and list-style-type

        const customList = document.createElement('custom-list')
        customList.setAttribute('depth', currentDepth)
        customList.setAttribute('list-id', listId)
        customList.setAttribute('list-style-type', listItemStyleType)
        customList.appendChild(child.cloneNode(true))
        result.push(customList)
      }
    }
  }
  return result
}
